import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { useQuery } from '@tanstack/react-query';
import { ProcessingUser } from './types';

export interface ProcessingUserFilters {
  userId?: string | null;
  email?: string | null;
  externalReference?: string | null;
  oldUserId?: string | null;
}

export interface Pagination {
  page: number;
  size: number;
}

interface SearchProcessingUserAggregatedData {
  count: number;
}

export interface SearchProcessingUsersResponse {
  processingUsers: ProcessingUser[];
  aggregatedData: SearchProcessingUserAggregatedData;
}

export interface ProcessingUserAlgorandTransaction {
  uuid: string;
  createdAt: Date;
  blockchainTransactionId: string;
  amount: number;
  networkFee: number;
  status: string;
  senderAddress: string;
  receiverAddress: string;
}

export interface ProcessingUserAlgorandTransactionsResponse {
  walletAddress?: string | null;
  transactions: ProcessingUserAlgorandTransaction[];
}

const queryKeys = {
  all: () => ['processing-users'] as const,
  algorandTransactions: (userId: string) =>
    [...queryKeys.all(), 'algorand-transactions', userId] as const,
};

export const searchProcessingUsers = async ({
  filters,
  pagination,
}: {
  filters: ProcessingUserFilters;
  pagination: Pagination;
}) => {
  return fetchAPI<SearchProcessingUsersResponse>('v1/processing-users:search', {
    method: 'POST',
    body: {
      filters,
      pagination,
    },
  });
};

export const exportTransactions = async (userId: string) => {
  const response = await fetchAPI(
    `v1/processing-users/${userId}/transactions`,
    {
      method: 'POST',
      stream: true,
    }
  );
  const safeChunks = response.map((c) => new Uint8Array(c));
  const blob = new Blob(safeChunks, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return blob;
};

export const getProcessingUserAlgorandTransactions = async (
  userId: string
): Promise<ProcessingUserAlgorandTransactionsResponse> => {
  const data = await fetchAPI<{
    walletAddress?: string | null;
    transactions: Array<
      Omit<ProcessingUserAlgorandTransaction, 'createdAt'> & {
        createdAt: string;
      }
    >;
  }>(`v1/processing-users/${userId}/algorand-transactions`);

  return {
    walletAddress: data.walletAddress,
    transactions: data.transactions.map((transaction) => ({
      ...transaction,
      createdAt: new Date(transaction.createdAt),
    })),
  };
};

export const useProcessingUserAlgorandTransactions = (
  userId?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: queryKeys.algorandTransactions(userId ?? ''),
    queryFn: async () => {
      if (!userId) {
        throw new Error('userId is required');
      }
      return getProcessingUserAlgorandTransactions(userId);
    },
    enabled: enabled && Boolean(userId),
  });
};
